import { useContext, useEffect, useRef, useSyncExternalStore } from "react";
import { useThread } from "@assistant-ui/react";
import { useArtifactLoader } from "../hooks/useArtifactLoader";
import { ArtifactLoaderContext } from "../contexts/ArtifactLoaderContext";
import { artifactStore } from "../adapters/artifactStore";
import {
  activeStreamingChatIds,
  getActiveThreadRemoteId,
  localToRemoteIdMap,
  subscribeToActiveThread,
} from "../adapters/threadListAdapter";

/**
 * Must be rendered inside AssistantRuntimeProvider and ArtifactLoaderContext.
 * Subscribes to the artifact store, loads artifacts for the current chat, and
 * registers refreshAfterStream so the adapter can call it when a stream ends.
 */
export function ArtifactLoaderGate() {
  const value = useContext(ArtifactLoaderContext);
  const threadId = useThread(
    (state) => (state as { threadId?: string }).threadId,
  );

  // Reactively track the active thread's remote ID so multi-chat mode picks
  // up chat switches immediately (module-level var is not state, so we need
  // useSyncExternalStore to make it reactive).
  const activeRemoteId = useSyncExternalStore(
    subscribeToActiveThread,
    getActiveThreadRemoteId,
    () => undefined,
  );

  // Resolve the server chat ID, preferring the currentChatIdRef (single-chat
  // & newly-created chats), then the reactive activeRemoteId (multi-chat
  // thread switches), then the localToRemoteIdMap fallback.
  const chatId =
    value?.currentChatIdRef?.current ??
    activeRemoteId ??
    (threadId ? (localToRemoteIdMap.get(threadId) ?? null) : null) ??
    null;

  // Close the artifact panel when switching to a *different* server chat.
  // Skip when `prev` is null: that is first mount or a remount (e.g. thread
  // reload after a message bumps `committed.key`) — same chatId must not
  // close the panel or users lose the artifact they were viewing mid-update.
  // Also skip when the destination chat is mid-stream (adapter is writing).
  const prevChatIdRef = useRef<string | null>(null);
  if (chatId !== prevChatIdRef.current) {
    const prev = prevChatIdRef.current;
    prevChatIdRef.current = chatId;
    const switchedToAnotherChat =
      prev !== null && chatId !== null && prev !== chatId;
    if (
      switchedToAnotherChat &&
      (!chatId || !activeStreamingChatIds.has(chatId))
    ) {
      artifactStore.closePanel();
    }
  }

  const { refreshAfterStream } = useArtifactLoader({
    client: value?.client ?? null,
    chatId,
    enableArtifacts: value?.enableArtifacts ?? false,
    initialOpenArtifactId: value?.initialOpenArtifactId ?? null,
    resolvedServerThreadId: value?.resolvedServerThreadId,
    onArtifactComplete: (id: string, artifactId?: string) =>
      value?.onArtifactCompleteRef?.current?.(id, artifactId),
  });

  useEffect(() => {
    if (!value?.refreshCallbackRef) return;
    value.refreshCallbackRef.current = refreshAfterStream;
    return () => {
      if (value.refreshCallbackRef) value.refreshCallbackRef.current = null;
    };
  }, [value?.refreshCallbackRef, refreshAfterStream]);

  return null;
}
